import { useState, useCallback, type ReactNode } from 'react';
import { DragDropContext, type DropResult } from '@hello-pangea/dnd';
import { usePipelineData, formatCurrency } from '../../../hooks/usePipelineData';
import type { Stage, Opportunity } from '../../../types';
import { StageColumn } from './StageColumn';
import styles from './PipelineBoard.module.css';

interface PipelineBoardProps {
  onViewDeal?: (id: string) => void;
  onEditDeal?: (id: string) => void;
}

export function PipelineBoard({
  onViewDeal,
  onEditDeal,
}: PipelineBoardProps): ReactNode {
  const {
    stages,
    opportunities,
    contacts,
    loading,
    error,
    refreshData,
    moveOpportunity,
  } = usePipelineData();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize
  useState(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  const handleDragEnd = useCallback(async (result: DropResult) => {
    const { draggableId, destination, source } = result;

    // Dropped outside a valid drop zone
    if (!destination) return;

    // Dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Find the target stage to get its default probability
    const targetStage = stages.find((s: Stage) => s.id === destination.droppableId);
    const newProbability = targetStage?.probability;

    await moveOpportunity(draggableId, destination.droppableId, newProbability);
  }, [stages, moveOpportunity]);

  const handleViewDeal = useCallback((id: string) => {
    if (onViewDeal) {
      onViewDeal(id);
    } else {
      // Default: navigate to deal view
      window.location.href = `/deals/${id}`;
    }
  }, [onViewDeal]);

  const handleEditDeal = useCallback((id: string) => {
    if (onEditDeal) {
      onEditDeal(id);
    } else {
      // Default: navigate to deal edit
      window.location.href = `/deals/${id}/edit`;
    }
  }, [onEditDeal]);

  // Calculate total pipeline value
  const totalPipelineValue = opportunities.reduce((sum: number, o: Opportunity) => sum + o.amount, 0);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
        <p>Loading pipeline...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>Error loading pipeline: {error}</p>
        <button onClick={refreshData} className={styles.retryButton}>
          Retry
        </button>
      </div>
    );
  }

  // Mobile view with tabs
  if (isMobile) {
    const activeStage = stages.find((s: Stage) => s.id === activeTab) || stages[0];

    return (
      <div className={styles.boardMobile}>
        <div className={styles.mobileHeader}>
          <h1 className={styles.title}>Pipeline</h1>
          <div className={styles.totalValue}>
            Total: {formatCurrency(totalPipelineValue)}
          </div>
        </div>

        <div className={styles.tabs}>
          {stages.map((stage: Stage) => {
            const count = opportunities.filter((o: Opportunity) => o.stage_id === stage.id).length;
            const isActive = activeStage?.id === stage.id;
            return (
              <button
                key={stage.id}
                className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(stage.id)}
                style={{ '--stage-color': stage.color } as React.CSSProperties}
              >
                <span className={styles.tabName}>{stage.name}</span>
                <span className={styles.tabCount}>{count}</span>
              </button>
            );
          })}
        </div>

        {activeStage && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className={styles.mobileColumn}>
              <StageColumn
                stage={activeStage}
                opportunities={opportunities}
                contacts={contacts}
                onViewDeal={handleViewDeal}
                onEditDeal={handleEditDeal}
              />
            </div>
          </DragDropContext>
        )}
      </div>
    );
  }

  // Desktop view with horizontal scroll
  return (
    <div className={styles.board}>
      <div className={styles.header}>
        <h1 className={styles.title}>Pipeline</h1>
        <div className={styles.headerRight}>
          <div className={styles.totalValue}>
            Total: {formatCurrency(totalPipelineValue)}
          </div>
          <button onClick={refreshData} className={styles.refreshButton} title="Refresh">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
          </button>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className={styles.columns}>
          {stages.map((stage: Stage) => (
            <StageColumn
              key={stage.id}
              stage={stage}
              opportunities={opportunities}
              contacts={contacts}
              onViewDeal={handleViewDeal}
              onEditDeal={handleEditDeal}
            />
          ))}
        </div>
      </DragDropContext>
    </div>
  );
}
